// react
import type { ReactNode } from 'react';
// libraries
import type {
  Control,
  FieldPath,
  FieldValues,
  UseControllerProps,
} from 'react-hook-form';
import { Controller } from 'react-hook-form';

// material ui
import { Box, TextField } from '@mui/material';
import type { TextFieldProps } from '@mui/material';

// relative
import RHFFieldLabel from './RHFFieldLabel';

export type RHFTextFieldProps<TFieldValues extends FieldValues> = {
  name: FieldPath<TFieldValues>;
  control: Control<TFieldValues>;
  label?: ReactNode;
  rules?: UseControllerProps<TFieldValues>['rules'];
} & Omit<
  TextFieldProps,
  | 'name'
  | 'defaultValue'
  | 'label'
  | 'error'
  | 'onChange'
  | 'value'
  | 'onBlur'
  | 'inputRef'
>;

export default function RHFTextField<TFieldValues extends FieldValues>({
  name,
  control,
  label,
  rules,
  required,
  id,
  ...textFieldProps
}: RHFTextFieldProps<TFieldValues>) {
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => {
        const inputId = id || field.name;

        return (
          <Box>
            {label ? (
              <RHFFieldLabel htmlFor={inputId} required={required}>
                {label}
              </RHFFieldLabel>
            ) : null}
            <TextField
              {...field}
              {...textFieldProps}
              id={inputId}
              required={required}
              variant="outlined"
              onChange={field.onChange}
              value={field.value ?? ''}
              error={!!fieldState.error}
              helperText={
                fieldState.error?.message ?? textFieldProps.helperText
              }
            />
          </Box>
        );
      }}
    />
  );
}
